import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { ChipsAutocompleteComponent } from '../chips-autocomplete/chips-autocomplete.component';
import { PortalService, UtilsService } from '../../services/index';
import { CONSTANTS_TOKEN } from '../../config/index';
import { AreaOfInterestHelper, AreaOfInterestItem } from '../../helpers/area-of-interest.helper';
import { SnackBar } from '../../enums/index';

export interface AreaOfInterestDialogData {
  initialSelected?: AreaOfInterestItem[];
  documentIds?: string[];
}

export interface AreaOfInterestDialogResult {
  selected?: AreaOfInterestItem[];
  assignSuccess?: boolean;
}

@Component({
  selector: 'educare-area-of-interest-dialog',
  templateUrl: './area-of-interest-dialog.component.html',
  styleUrl: './area-of-interest-dialog.component.scss',
  imports: [CommonModule, MatDialogModule, MatProgressSpinnerModule, TranslateModule, ChipsAutocompleteComponent]
})
export class AreaOfInterestDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<AreaOfInterestDialogComponent>);
  private readonly portalService = inject(PortalService);
  private readonly utils = inject(UtilsService);
  private readonly apiConstants = inject(CONSTANTS_TOKEN);
  private readonly data = inject<AreaOfInterestDialogData>(MAT_DIALOG_DATA, { optional: true });

  selectedItems: AreaOfInterestItem[] = this.data?.initialSelected?.length
    ? this.data.initialSelected.map((i) => ({ ...i }))
    : [];
  /** IDs/keys of pre-selected items (from backend or initial data). These do not show the remove (X) icon. */
  initialItemKeys = new Set<string | number>(
    this.data?.initialSelected?.map((i) => (i?.id != null ? i.id : (i?.name ?? '').toString())) ?? []
  );
  submitting = false;
  loadingExisting = false;

  /** Returns false for pre-selected items (no backend remove), true for newly added from dropdown. */
  isItemRemovableFn = (item: AreaOfInterestItem): boolean =>
    !this.initialItemKeys.has(item?.id != null ? item.id : (item?.name ?? '').toString());

  private readonly lookupCache: AreaOfInterestItem[] = [];

  ngOnInit(): void {
    this.loadExistingAreasOfInterest();
  }

  /**
   * When opening to assign more AOI to document(s), pre-populate with already-assigned areas of interest.
   * Fetches document details for each documentId and merges areasOfInterest (union by id).
   */
  private loadExistingAreasOfInterest(): void {
    const documentIds = this.data?.documentIds;
    const getDocumentEndpoint = this.apiConstants?.api?.privateEndpoints?.getDocument;
    if (!documentIds?.length || !getDocumentEndpoint) {
      return;
    }
    this.loadingExisting = true;
    const requests = documentIds.map((id) =>
      this.portalService.get(this.utils.getEncryptedUrls(getDocumentEndpoint) + '/' + encodeURIComponent(id)).pipe(
        map((res: any) => (res?.data ?? res)?.areasOfInterest ?? []),
        catchError(() => of([]))
      )
    );
    forkJoin(requests).subscribe({
      next: (results) => {
        const seen = new Set<number | string>();
        const merged: AreaOfInterestItem[] = [];
        for (const list of results) {
          for (const item of list) {
            const id = item?.id ?? item?.area_of_interest_id;
            const key = id != null ? Number(id) : (item?.name ?? '').toString();
            if (key === '' || seen.has(key)) continue;
            seen.add(key);
            merged.push({
              id: id ?? key,
              name: (item?.name ?? item?.ar_name ?? '').toString().trim() || 'Unknown'
            });
          }
        }
        if (merged.length > 0) {
          this.selectedItems = merged;
          this.initialItemKeys = new Set(merged.map((i) => (i?.id != null ? i.id : (i?.name ?? '').toString())));
          AreaOfInterestHelper.mergeLookupCache(this.lookupCache, merged);
        }
        this.loadingExisting = false;
      },
      error: () => {
        this.loadingExisting = false;
      }
    });
  }

  searchAreaOfInterest = (keyword: string): Observable<AreaOfInterestItem[]> => {
    const endpoint = this.apiConstants?.api?.privateEndpoints?.areasOfInterest;
    if (!endpoint) return of([]);

    const url = this.utils.getEncryptedUrls(endpoint) + `?query=${encodeURIComponent(keyword || '')}`;
    return this.portalService.get(url).pipe(
      map((res: any) => AreaOfInterestHelper.parseApiResponse(res)),
      tap((items) => AreaOfInterestHelper.mergeLookupCache(this.lookupCache, items))
    );
  };

  onSelected(selected: AreaOfInterestItem[]): void {
    this.selectedItems = selected;
  }

  onConfirm(): void {
    const documentIds = this.data?.documentIds ?? [];

    if (!documentIds.length) {
      this.dialogRef.close({ selected: this.selectedItems } as AreaOfInterestDialogResult);
      return;
    }

    if (!this.selectedItems.length) {
      this.utils.openSnackBar(
        this.utils.translation('snackbar.areaOfInterestSelectAtLeastOne'),
        'Close',
        SnackBar.Error
      );
      return;
    }

    this.assignAoi(documentIds);
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  private assignAoi(documentIds: string[]): void {
    const endpoint = this.apiConstants?.api?.privateEndpoints?.assignAoi;
    if (!endpoint) {
      this.showError(this.utils.translation('snackbar.areaOfInterestAssignFailed'));
      return;
    }

    this.submitting = true;
    const payload = {
      document_ids: documentIds,
      area_of_interest_ids: AreaOfInterestHelper.toPositiveIntegerIds(this.selectedItems),
      area_of_interest_names: AreaOfInterestHelper.toCustomNames(this.selectedItems)
    };

    this.portalService.post(this.utils.getEncryptedUrls(endpoint), payload).subscribe({
      next: () => {
        this.utils.openSnackBar(
          this.utils.translation('snackbar.areaOfInterestAssignedSuccess'),
          'Close',
          SnackBar.Success
        );
        this.dialogRef.close({ selected: this.selectedItems, assignSuccess: true } as AreaOfInterestDialogResult);
      },
      error: (err) => {
        this.submitting = false;
        this.showError(err?.error?.message || this.utils.translation('snackbar.areaOfInterestAssignFailed'));
      }
    });
  }

  private showError(message: string): void {
    this.utils.openSnackBar(message, 'Close', SnackBar.Error);
  }
}
